<template>
  <v-card outlined class="client-notes">
    <v-card-title class="notes-title-row">Notes</v-card-title>

    <v-divider></v-divider>

    <v-card-text class="notes-form-wrap">
      <v-row dense class="ma-0 notes-form-row" align="end">
        <v-col cols="12" class="px-0 pr-md-2 date-col">
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="noteDateDisplay"
                placeholder="Date"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                hide-details
                class="date-field"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="noteDate"
              no-title
              scrollable
              @input="dateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

          <v-col cols="12" class="px-0 px-md-2 note-col">
          <v-textarea
            v-model="noteText"
            placeholder="Write a note"
            outlined
            dense
              rows="1"
            hide-details
            class="note-input"
          ></v-textarea>
        </v-col>

        <v-col cols="12" class="px-0 pl-md-2 action-col">
          <v-btn
            color="primary"
            block
            class="save-note-btn"
            :loading="saving"
            :disabled="saving"
            @click="saveNote()"
          >
            {{ editingNoteId ? 'Update note' : 'Add note' }}
          </v-btn>
        </v-col>
      </v-row>

      <v-alert v-if="error" type="error" dense class="mt-3 mb-0">
        {{ error }}
      </v-alert>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-text class="notes-list-wrap">
      <div class="notes-scroll">
        <div v-if="visibleNotes.length === 0" class="notes-empty">
          No notes yet.
        </div>

        <div v-for="note in visibleNotes" :key="note.id" class="note-row">
          <div class="note-row-date">{{ formatDate(note.date) }}</div>
          <div class="note-row-text">{{ note.text }}</div>
          <div class="note-row-actions">
            <v-icon small color="primary" class="mr-2" @click="startEdit(note)">
              mdi-pencil
            </v-icon>
            <v-icon small color="red" @click="deleteNote(note)">
              mdi-delete
            </v-icon>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import msalAuthHandler from "vue-msal-wrapper";

export default {
	props: ["clientId", "clientNotes"],
	data() {
		return {
			notes: [],
			noteDate: this.getTodayDate(),
			noteText: "",
			editingNoteId: null,
			dateMenu: false,
			saving: false,
			error: null,
		};
	},
	computed: {
		sortedNotes() {
			return [...this.notes].sort((a, b) => {
				const dateDiff = new Date(`${b.date}T00:00:00`) - new Date(`${a.date}T00:00:00`);
				if (dateDiff !== 0) {
					return dateDiff;
				}
				return b.id - a.id;
			});
		},
		visibleNotes() {
			return this.sortedNotes;
		},
		noteDateDisplay() {
			if (!this.noteDate) {
				return "";
			}
			return this.formatDate(this.noteDate);
		},
	},
	watch: {
		clientNotes: {
			immediate: true,
			handler(newVal) {
				this.notes = Array.isArray(newVal) ? [...newVal] : [];
			},
		},
	},
	methods: {
		getTodayDate() {
			const now = new Date();
			const offset = now.getTimezoneOffset();
			const local = new Date(now.getTime() - offset * 60000);
			return local.toISOString().slice(0, 10);
		},
		formatDate(dateString) {
			if (!dateString) {
				return "";
			}
			return new Date(`${dateString}T00:00:00`).toLocaleDateString();
		},
		resetForm() {
			this.noteDate = this.getTodayDate();
			this.noteText = "";
			this.editingNoteId = null;
			this.error = null;
		},
		startEdit(note) {
			this.editingNoteId = note.id;
			this.noteDate = note.date;
			this.noteText = note.text;
			this.error = null;
			this.dateMenu = false;
		},
		async saveNote() {
			const trimmedText = this.noteText.trim();
			if (!this.noteDate || !trimmedText) {
				this.error = "Please provide both a date and note text";
				return;
			}

			this.saving = true;
			this.error = null;

			const payload = {
				clientId: this.clientId,
				date: this.noteDate,
				text: trimmedText,
			};

			try {
				const api = msalAuthHandler.authenticatedApi(this.$store.state.apiUrl, "backend");
				const response = this.editingNoteId
					? await api.patch(`note/${this.editingNoteId}`, JSON.stringify(payload))
					: await api.post(`note`, JSON.stringify(payload));

				if (response?.data?.data) {
					if (this.editingNoteId) {
						this.notes = this.notes.map((note) =>
							note.id === this.editingNoteId ? response.data.data : note
						);
					} else {
						this.notes = [response.data.data, ...this.notes];
					}
					this.$emit("updated", [...this.notes]);
					this.resetForm();
				}
			} catch (err) {
				console.error("Error saving note:", err);
				this.error = "Failed to save note";
			} finally {
				this.saving = false;
			}
		},
		async deleteNote(note) {
			const confirmed = window.confirm("Delete this note?");
			if (!confirmed) {
				return;
			}

			this.saving = true;
			this.error = null;

			try {
				const api = msalAuthHandler.authenticatedApi(this.$store.state.apiUrl, "backend");
				await api.delete(`note/${note.id}`, {});
				this.notes = this.notes.filter((item) => item.id !== note.id);
				this.$emit("updated", [...this.notes]);
				if (this.editingNoteId === note.id) {
					this.resetForm();
				}
			} catch (err) {
				console.error("Error deleting note:", err);
				this.error = "Failed to delete note";
			} finally {
				this.saving = false;
			}
		},
	},
};
</script>

<style scoped>
.client-notes {
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-top: -36px;
  margin-bottom: 36px;
}

.notes-title-row {
  padding-top: 12px;
  padding-bottom: 12px;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
}

.notes-form-wrap {
  padding-top: 12px;
  padding-bottom: 12px;
  background: #fafbfc;
}

.notes-form-row {
  display: flex;
  flex-wrap: nowrap;
}

.date-col {
  flex: 0 0 210px;
  max-width: 210px;
}

.note-col {
  flex: 1 1 auto;
  max-width: none;
}

.action-col {
  flex: 0 0 170px;
  max-width: 170px;
}

.date-field ::v-deep .v-input__slot {
  min-height: 42px;
}

.note-input ::v-deep .v-input__slot {
  min-height: 42px;
}

.save-note-btn {
  min-height: 42px;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.notes-list-wrap {
  padding-top: 8px;
  padding-bottom: 8px;
}

.notes-scroll {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  background: #fff;
  max-height: 142px;
  overflow-y: auto;
  overflow-x: hidden;
}

.notes-scroll::-webkit-scrollbar {
  width: 7px;
}

.notes-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.notes-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.18);
  border-radius: 4px;
}

.notes-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.28);
}

.notes-empty {
  padding: 16px;
  text-align: center;
  color: rgba(0, 0, 0, 0.54);
  font-size: 0.85rem;
}

.note-row {
  display: grid;
  grid-template-columns: 120px 1fr 70px;
  gap: 10px;
  align-items: start;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.note-row:last-child {
  border-bottom: none;
}

.note-row-date {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
}

.note-row-text {
  font-size: 0.85rem;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.87);
  white-space: pre-wrap;
  word-break: break-word;
}

.note-row-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

@media (max-width: 960px) {
  .notes-form-row {
    flex-wrap: wrap;
  }

  .date-col,
  .note-col,
  .action-col {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .save-note-btn {
    min-height: 44px;
  }

  .note-row {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .note-row-actions {
    justify-content: flex-start;
  }
}
</style>
